import { useEffect, useState } from "react";
import {
  Sparkles,
  Building,
  Globe,
  Calendar,
  Users,
  CheckCircle2,
  AlertTriangle,
  ListChecks,
  Mail,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  MessageSquare,
} from "lucide-react";
import api from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

function tokenize(jurisdiction) {
  return new Set(
    (jurisdiction || "")
      .toLowerCase()
      .split(/[^a-z]+/)
      .filter((t) => t.length >= 4 && !["city", "town", "county", "village", "borough", "and", "the", "department"].includes(t))
  );
}

function highlight(text, tokens) {
  if (!tokens || tokens.size === 0) return text;
  let result = text;
  tokens.forEach((t) => {
    const re = new RegExp(`(${t})`, "ig");
    result = result.replace(re, "<mark>$1</mark>");
  });
  return result;
}

export default function AhjPlaybook({ ahjKey, scope = "county", jurisdictionFallback }) {
  const [state, setState] = useState({ data: null, error: null });
  const [showComms, setShowComms] = useState(false);

  useEffect(() => {
    if (!ahjKey) return undefined;
    let alive = true;
    api.get(`/ahj/playbook/${ahjKey}?scope=${scope}`)
      .then((r) => alive && setState({ data: r.data, error: null }))
      .catch((e) => {
        if (!alive) return;
        const code = e.response?.status === 404 ? "no-intel" : (e.response?.data?.detail || "Could not load playbook");
        setState({ data: null, error: code });
      });
    return () => { alive = false; };
  }, [ahjKey, scope]);

  const { data, error } = state;

  if (error === "no-intel") return null;

  if (!data) {
    return (
      <section
        data-testid="ahj-playbook-loading"
        className="border border-slate-200 rounded-2xl p-6 mb-8 bg-white/60"
      >
        <div className="text-xs font-mono uppercase tracking-[0.18em] text-slate-400">Loading AHJ playbook…</div>
      </section>
    );
  }

  const { intel, playbook } = data;
  const tokens = tokenize(playbook.overview.jurisdiction || jurisdictionFallback);

  return (
    <section
      data-testid="ahj-playbook"
      id="ahj-playbook"
      className="relative overflow-hidden border border-indigo-200 rounded-2xl mb-8 bg-gradient-to-br from-white via-white to-indigo-50/40 scroll-mt-24 transition-shadow"
    >
      {/* Header strip */}
      <div className="px-6 md:px-8 pt-6 md:pt-7 pb-5 border-b border-indigo-100">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-600 text-white flex items-center justify-center">
              <ListChecks className="w-5 h-5" />
            </div>
            <div>
              <div className="font-outfit text-xl font-bold text-slate-900">AHJ Playbook</div>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-indigo-600">
                {scope === "city" ? "City-level submission flow" : "County-level submission flow"}
              </div>
            </div>
          </div>
          <Badge
            data-testid="ahj-playbook-source"
            className="bg-white border-indigo-300 text-indigo-700 font-mono text-[10px]"
          >
            <Users className="w-2.5 h-2.5 mr-1" />
            {intel.source_client}
          </Badge>
        </div>
        <h3 className="mt-4 font-outfit text-lg text-slate-900 font-medium">
          {playbook.overview.jurisdiction}
        </h3>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-0 lg:gap-0">
        {/* Left rail — overview & contact */}
        <aside className="lg:col-span-2 border-r border-indigo-100 px-6 md:px-8 py-6 bg-white/50">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 mb-3">Overview</div>
          <div className="space-y-4">
            <InfoBlock
              icon={<Globe className="w-3.5 h-3.5" />}
              label="Portal / system"
              value={playbook.overview.portal}
              isLink={playbook.overview.portal && playbook.overview.portal.startsWith("http")}
            />
            {playbook.primary_contact && (
              <InfoBlock
                icon={<Mail className="w-3.5 h-3.5" />}
                label="Primary contact"
                value={playbook.primary_contact.email}
                mono
                isMail
              />
            )}
            {playbook.overview.status && (
              <InfoBlock
                icon={<Building className="w-3.5 h-3.5" />}
                label="Status keyword"
                value={playbook.overview.status}
              />
            )}
            {playbook.overview.last_seen && (
              <InfoBlock
                icon={<Calendar className="w-3.5 h-3.5" />}
                label="Last seen"
                value={playbook.overview.last_seen}
                mono
              />
            )}
          </div>
        </aside>

        {/* Right column — submission flow + tips */}
        <div className="lg:col-span-3 px-6 md:px-8 py-6">
          {/* Submission steps */}
          <div data-testid="ahj-playbook-steps">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded bg-indigo-100 text-indigo-700 flex items-center justify-center">
                <CheckCircle2 className="w-3.5 h-3.5" />
              </div>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">
                Submission flow ({playbook.submission_steps.length} {playbook.submission_steps.length === 1 ? "step" : "steps"})
              </div>
            </div>
            <ol className="relative pl-6 space-y-4">
              <span className="absolute left-2 top-1.5 bottom-1.5 w-px bg-indigo-200" aria-hidden />
              {playbook.submission_steps.map((step, i) => (
                <li key={i} className="relative" data-testid={`step-${i}`}>
                  <span className="absolute -left-6 top-0.5 w-4 h-4 rounded-full bg-white border-2 border-indigo-400 text-[10px] font-mono text-indigo-700 flex items-center justify-center">
                    {i + 1}
                  </span>
                  <p className="text-sm text-slate-700 leading-relaxed">{step}</p>
                </li>
              ))}
            </ol>
          </div>

          {/* Tips & warnings */}
          {playbook.tips.length > 0 && (
            <div className="mt-7 pt-6 border-t border-indigo-100" data-testid="ahj-playbook-tips">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded bg-amber-100 text-amber-700 flex items-center justify-center">
                  <AlertTriangle className="w-3.5 h-3.5" />
                </div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">
                  Field tips &amp; gotchas
                </div>
              </div>
              <ul className="space-y-2.5">
                {playbook.tips.map((tip, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 px-4 py-3 bg-amber-50/70 border border-amber-200 rounded-lg"
                    data-testid={`tip-${i}`}
                  >
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 flex-none" />
                    <span className="text-sm text-slate-800 leading-relaxed">{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Related communications */}
          {playbook.related_communications_count > 0 && (
            <div className="mt-7 pt-6 border-t border-indigo-100" data-testid="ahj-playbook-comms">
              <button
                onClick={() => setShowComms((v) => !v)}
                className="w-full flex items-center justify-between text-left group"
                data-testid="ahj-playbook-comms-toggle"
              >
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded bg-emerald-100 text-emerald-700 flex items-center justify-center">
                    <MessageSquare className="w-3.5 h-3.5" />
                  </div>
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">
                      Related communications
                    </div>
                    <div className="text-sm text-slate-700 mt-0.5">
                      <strong>{playbook.related_communications_count}</strong> matched contacts from prior email imports
                    </div>
                  </div>
                </div>
                {showComms ? (
                  <ChevronUp className="w-4 h-4 text-slate-400 group-hover:text-slate-700" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-slate-400 group-hover:text-slate-700" />
                )}
              </button>
              {showComms && (
                <div className="mt-4 space-y-3">
                  {playbook.related_communications.map((c) => (
                    <div
                      key={c.email}
                      className="px-4 py-3 bg-white border border-emerald-100 rounded-lg"
                      data-testid={`comm-${c.email}`}
                    >
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-900">{c.name || c.email}</div>
                          <a
                            href={`mailto:${c.email}`}
                            className="text-xs text-slate-500 font-mono hover:text-slate-900 break-all"
                          >
                            {c.email}
                          </a>
                        </div>
                        <Badge variant="outline" className="font-mono text-[10px]">
                          {c.count}× exchanges
                        </Badge>
                      </div>
                      {c.sample_subjects && c.sample_subjects.length > 0 && (
                        <ul className="mt-2 space-y-1">
                          {c.sample_subjects.map((s, i) => (
                            <li
                              key={i}
                              className="text-xs text-slate-600 leading-snug"
                              dangerouslySetInnerHTML={{ __html: `&ldquo;${highlight(s, tokens)}&rdquo;` }}
                            />
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* CTA row */}
          {playbook.overview.portal_url && (
            <div className="mt-7 pt-6 border-t border-indigo-100">
              <Button
                asChild
                data-testid="ahj-playbook-open-portal"
                className="bg-indigo-600 hover:bg-indigo-500 text-white"
              >
                <a href={playbook.overview.portal_url} target="_blank" rel="noopener noreferrer">
                  Open jurisdiction portal
                  <ExternalLink className="w-3.5 h-3.5 ml-2" />
                </a>
              </Button>
            </div>
          )}
        </div>
      </div>

      <style>{`
        section[data-testid="ahj-playbook"] mark {
          background: rgba(99, 102, 241, 0.18);
          color: rgb(67, 56, 202);
          padding: 0 2px;
          border-radius: 2px;
        }
      `}</style>
    </section>
  );
}

function InfoBlock({ icon, label, value, mono, isLink, isMail }) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="w-7 h-7 rounded bg-white border border-indigo-100 flex items-center justify-center flex-none text-indigo-600 mt-0.5">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">{label}</div>
        {isLink ? (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className={`text-sm text-indigo-700 hover:text-indigo-900 underline break-all ${mono ? "font-mono" : ""}`}
          >
            {value}
          </a>
        ) : isMail ? (
          <a
            href={`mailto:${value}`}
            className={`text-sm text-indigo-700 hover:text-indigo-900 break-all ${mono ? "font-mono" : ""}`}
          >
            {value}
          </a>
        ) : (
          <div className={`text-sm text-slate-900 ${mono ? "font-mono break-all" : ""}`}>{value}</div>
        )}
      </div>
    </div>
  );
}
